// Check-in access token (STI-3024 / STI-2954 F-19).
//
// The check-in link carries `?t=<token>` (or a short /c/<code> link that is exchanged for one). The
// backend binds that token to one appointment and patient and, once enforcement is on, refuses
// check-in calls without it. We keep tokens for the tab in sessionStorage, one per appointment, and
// send the one for the page's appointment as X-Checkin-Token on every check-in call. A token is never
// sent for another appointment: a tab that opened appointment A's link and then B's must not offer
// A's token for B.
//
// Capture is lazy: getCheckinToken() reads `t` from the address bar the first time it is asked.
// Child components fire their API calls from effects that run before a parent's effects, so capturing
// in a parent effect would let the first calls go out without the token.

export const CHECKIN_TOKEN_HEADER = 'X-Checkin-Token';
export const CHECKIN_API_PREFIX = '/api/athena/digital-checkin/';
const STORAGE_KEY = 'checkinAccessToken';
const QUERY_PARAM = 't';

/** The backend's refusal for a token whose appointment was cancelled or rescheduled (410). */
export const CHECKIN_INACTIVE = 'appointment_inactive';

/** The backend's refusal reasons for a missing, bad, mismatched or inactive token (CheckinAccessInterceptor). */
export const CHECKIN_REFUSALS = [
  'missing_token',
  'invalid_token',
  'appointment_mismatch',
  'patient_mismatch',
  CHECKIN_INACTIVE,
];

/** Kept tokens by appointment id, and the appointment of the last one kept. */
type Stored = { tokens: Record<string, string>; last: string | null };

// Fallback when sessionStorage is blocked (private mode, disabled site data).
let memory: Stored = { tokens: {}, last: null };

/**
 * The appointment id inside a token, or null if it is not one. The token is
 * base64url(`tenant|athenaAppointmentId|athenaPatientId|expiresEpoch`) + "." + base64url(mac)
 * (CheckinAccessTokenService). Only read to pick which appointment a token belongs to; the backend
 * still verifies it.
 */
export function tokenAppointmentId(token: string): string | null {
  const parts = token.split('.');
  if (parts.length !== 2 || !parts[0] || !parts[1]) {
    return null;
  }
  try {
    const base64 = parts[0].replace(/-/g, '+').replace(/_/g, '/');
    const fields = window.atob(base64 + '='.repeat((4 - (base64.length % 4)) % 4)).split('|');
    return fields.length === 4 && fields[1] ? fields[1] : null;
  } catch {
    return null;
  }
}

function parse(raw: string | null): Stored | null {
  if (!raw) {
    return null;
  }
  try {
    const value = JSON.parse(raw);
    if (value && typeof value === 'object' && value.tokens && typeof value.tokens === 'object') {
      return { tokens: value.tokens, last: typeof value.last === 'string' ? value.last : null };
    }
  } catch {
    // Not JSON: a single token kept by the build before per-appointment storage.
  }
  const appointmentId = tokenAppointmentId(raw);
  return appointmentId ? { tokens: { [appointmentId]: raw }, last: appointmentId } : null;
}

function read(): Stored {
  try {
    return parse(window.sessionStorage.getItem(STORAGE_KEY)) ?? memory;
  } catch {
    return memory;
  }
}

function write(stored: Stored): void {
  memory = stored;
  try {
    if (Object.keys(stored.tokens).length === 0) {
      window.sessionStorage.removeItem(STORAGE_KEY);
    } else {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
    }
  } catch {
    // memory already holds it.
  }
}

/** Keeps the token under its own appointment. A string that is not a token is dropped. */
export function setCheckinToken(token: string): void {
  const appointmentId = tokenAppointmentId(token);
  if (!appointmentId) {
    return;
  }
  const stored = read();
  write({ tokens: { ...stored.tokens, [appointmentId]: token }, last: appointmentId });
}

/** Drops the token for one appointment, or every kept token when no id is given. */
export function clearCheckinToken(appointmentId?: string | null): void {
  if (!appointmentId) {
    write({ tokens: {}, last: null });
    return;
  }
  const stored = read();
  const tokens = { ...stored.tokens };
  delete tokens[appointmentId];
  write({ tokens, last: stored.last === appointmentId ? null : stored.last });
}

function captureFromUrl(): void {
  const fromUrl = new URLSearchParams(window.location.search).get(QUERY_PARAM);
  if (fromUrl && fromUrl.trim()) {
    setCheckinToken(fromUrl.trim());
  }
}

/**
 * The token for this appointment, or null if this tab holds none for it. A token in the address bar
 * is kept first (a new link). With no appointment id (a page whose path names none) it is the last
 * token kept: such a page has no other appointment to send it for.
 */
export function getCheckinToken(appointmentId: string | null): string | null {
  captureFromUrl();
  const stored = read();
  if (appointmentId) {
    return stored.tokens[appointmentId] ?? null;
  }
  return stored.last ? stored.tokens[stored.last] ?? null : null;
}

/**
 * Keeps the token, then drops `t` from the address bar so it does not stay in history. Other params
 * are kept. Capturing first means a page that makes no check-in call before this runs still has it.
 */
export function stripCheckinTokenFromUrl(): void {
  const params = new URLSearchParams(window.location.search);
  if (!params.has(QUERY_PARAM)) {
    return;
  }
  captureFromUrl();
  params.delete(QUERY_PARAM);
  const search = params.toString();
  const url = window.location.pathname + (search ? `?${search}` : '') + window.location.hash;
  window.history.replaceState(window.history.state, '', url);
}

export function isCheckinApiUrl(url: string | undefined): boolean {
  return !!url && url.includes(CHECKIN_API_PREFIX);
}

/** The appointment id in /appointments/:id, /start-checkin/:id or /checkin-verify/:id, if any. */
export function appointmentIdFromPath(pathname: string): string | null {
  const match = pathname.match(/^\/(?:appointments|start-checkin|checkin-verify)\/([^/?#]+)/);
  if (!match) {
    return null;
  }
  try {
    return decodeURIComponent(match[1]);
  } catch {
    // A malformed % sequence (hand-edited URL). Called from the axios error interceptor, so it must
    // not throw and replace the original error.
    return null;
  }
}

/**
 * Where to send the patient after the backend refused a check-in call for its token, or null when the
 * response is not such a refusal. The verify page lets them get a new token with their date of birth,
 * or, for a cancelled or rescheduled appointment (410), says so instead of asking.
 */
export function checkinRefusalRedirect(
  url: string | undefined,
  status: number | undefined,
  data: unknown,
  pathname: string,
): string | null {
  if (!isCheckinApiUrl(url) || (status !== 401 && status !== 403 && status !== 410)) {
    return null;
  }
  const reason = (data as { error?: unknown } | null | undefined)?.error;
  if (typeof reason !== 'string' || !CHECKIN_REFUSALS.includes(reason)) {
    return null;
  }
  const appointmentId = appointmentIdFromPath(pathname);
  if (!appointmentId || pathname.startsWith('/checkin-verify/')) {
    return null;
  }
  const verify = `/checkin-verify/${encodeURIComponent(appointmentId)}`;
  return reason === CHECKIN_INACTIVE ? `${verify}?reason=${CHECKIN_INACTIVE}` : verify;
}
